import { MenuLink, useStyles } from "./HeaderSubmenu";
import {
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Typography,
} from "@material-ui/core";
import { ListItem } from "app/components/common/ListItem";
import Tooltip from "app/components/common/Tooltip";
import {
  MANAGE_DASHBOARDS_PATH,
  canViewManageDashboards,
  dashboardBoardPath,
} from "app/routes/dashboard/routes/manage-dashboards/constants";
import { selMenuDashboards } from "app/routes/dashboard/routes/manage-dashboards/slice/manageDashboardsSlice";
import { getMenuDashboards } from "app/routes/dashboard/routes/manage-dashboards/slice/manageDashboardsThunk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selGrants, selSecret } from "store/slices";

/* -------------------------------------------------------------------------- */
const ENROUTE_BOARD_PATH = "/app/dashboard";
const ITEM_HEIGHT = 40;
const MAX_VISIBLE_DASHBOARDS = 8;

/* -------------------------------------------------------------------------- */
// Header "Dashboard" menu: Enroute Board, the user's dashboards and Manage
// Dashboards. Rendered in a portal so page content never covers it.
const DashboardMenu = ({ item, selected, className }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const dashboards = useSelector(selMenuDashboards);
  const grants = useSelector(selGrants);
  const secret = useSelector(selSecret);
  const canManage = canViewManageDashboards(grants);

  // Dashboards are scoped to the assignment, so reload on account switch.
  useEffect(() => {
    dispatch(getMenuDashboards());
  }, [dispatch, secret]);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const close = useCallback(() => setOpen(false), []);
  const onClickAway = useCallback(
    (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      close();
    },
    [close]
  );

  return (
    <>
      <div ref={anchorRef}>
        <ListItem
          onClick={toggle}
          underline={true}
          selected={selected}
          className={className}
          text={item.name}
          startAdornment={item.icon}
        />
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className={classes.popper}
      >
        <ClickAwayListener onClickAway={onClickAway}>
          <Paper className={classes.paper} elevation={4}>
            <MenuLink
              to={ENROUTE_BOARD_PATH}
              label="Enroute Board"
              active={pathname === ENROUTE_BOARD_PATH}
              onClick={close}
            />
            <Divider />
            <div
              className={classes.dashboards}
              style={{ maxHeight: ITEM_HEIGHT * MAX_VISIBLE_DASHBOARDS }}
            >
              {dashboards.length ? (
                dashboards.map((dashboard) => (
                  <Tooltip
                    key={dashboard.id}
                    content={dashboard.name || ""}
                    placement="right"
                  >
                    <div>
                      <MenuLink
                        to={dashboardBoardPath(dashboard.id)}
                        label={dashboard.name}
                        active={pathname === dashboardBoardPath(dashboard.id)}
                        onClick={close}
                      />
                    </div>
                  </Tooltip>
                ))
              ) : (
                <Typography className={classes.empty}>
                  No dashboards yet
                </Typography>
              )}
            </div>
            {canManage && (
              <>
                <Divider />
                <MenuLink
                  to={MANAGE_DASHBOARDS_PATH}
                  label="Manage Dashboards"
                  active={pathname.startsWith(MANAGE_DASHBOARDS_PATH)}
                  onClick={close}
                />
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default React.memo(DashboardMenu);
